import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import cx from "classnames";
import { getCourseStudents, remoteCancelStudent, remoteDeleteStudent } from "../../../queries/students";
import { IconTitle } from "../Titles/IconTitle";
import { EndTermToggle } from "../../EndTermToggle";
import { UserOptionToggle } from "../../UserOptionToggle";
import ReactTooltip from "react-tooltip";
import moment from "moment";
import EventStudent from "../../../pages/courses/components/eventStudent";
import { addGlobalMessage } from "../../../actions/globalmessages";
import { useDispatch } from "react-redux";
import { WrapInDialogButton } from "../../WrapInDialogButton";
import ImportStudent from "../../../pages/courses/components/importStudent";
import FindStudent from "../../../pages/courses/components/findStudent";
import { downloadFile, smoothScrollToTop } from "../../../functions";
import { AttendanceToggle } from "../../AttendanceToggle";
import NotifyUserDialog from "../../../pages/courses/NotifyUserDialog";
import { buildCertificationTree, filterUnrelatedCertifications } from "../../../utils/certifications";

const canAddStudents = (courseDetail) => {
    return (courseDetail.is_editable && courseDetail.students_can_be_added) || false;
};

const downloadCertificates = (course, user) => {
    downloadFile(`/v1/courses/${course}/certificates/?user=${user}`);
};

let refreshFn;
let courseUUID;

const RowStructure = (props) => {
    const dispatch = useDispatch();

    const [displayStudent, setDisplayStudent] = useState(false);

    if (props.contextData.courseDetail) courseUUID = props.contextData.courseDetail.uuid;

    const removeStudentFromCourse = useCallback(
        async (uuid, name) => {
            const courseUUID = props?.contextData?.courseDetail?.uuid;

            const resultDescription = `De student '${name}' is van de cursus af gehaald`;

            let result = await remoteDeleteStudent(courseUUID, uuid);

            if (result?.errors?.length > 0) {
                dispatch(
                    addGlobalMessage({
                        title: result?.errors?.response?.data || "Er is een onbekende fout opgetreden",
                        type: "error",
                        icon: "fa fa-exclamation-triangle"
                    })
                );
            } else {
                dispatch(
                    addGlobalMessage({
                        title: resultDescription,
                        link: `/courses/edit/${courseUUID}/students/`
                    })
                );
                if (typeof props?.refresh === "function") {
                    props?.contextData?.endTermsMutate();
                    props?.refresh();
                    props.unSelectInBulk(uuid);
                }
            }
        },
        [props?.refresh, props?.contextData?.courseDetail?.uuid]
    );

    useEffect(() => {
        refreshFn = props.refresh;
    }, [props.contextData?.courseDetail, props.contextData?.shouldRefreshStudents]);

    useEffect(() => {
        // props.refresh();
    }, [props.shouldRefreshStudents]);

    /**
     * We want to display the tree of competence profiles with the corresponding
     * certificates for this user.
     */
    // The certificates and competence profile have been fetched in the CourseDetail and
    // put into the context:
    const { certificates, competenceProfile } = props.contextData;

    // From the available_certifications on the student data we build a tree:
    const tree = buildCertificationTree(props.available_certifications);

    // This tree is unfiltered and contains competence profiles that are not really
    // related to the profile of this course. So we use the filterUnrelatedCertifications
    // function to filter out those that are not related and add the certificates to the
    // tree:

    // We keep a set of the profiles with a certificate, so we can display a count:
    let certifiedProfiles = new Set();
    let displayedCertificates = new Set();

    let userCertificateTree = [];
    // Walk through the nested tree and link any certificates:
    if (tree && certificates) {
        // filter those that are ready and belong to this user:
        const userCertificates = certificates.results.filter((c) => c.user.uuid === props.uuid); // c.state === "ready"
        const extendBranchWithCertificates = ({ children, ...node }, i) => {
            let certificate = null;
            if (!displayedCertificates.has(node.competence_profile.uuid)) {
                certificate = userCertificates.find((c) => c.competence_profile.uuid === node.competence_profile.uuid) || null;
                if (certificate) {
                    // We linked a certificate
                    certifiedProfiles.add(node.competence_profile.uuid);
                    displayedCertificates.add(node.competence_profile.uuid); // Track that we have displayed this certificate
                }
            }
            return {
                ...node,
                certificate,
                children: children ? filterUnrelatedCertifications(competenceProfile, children).map(extendBranchWithCertificates) : null
            };
        };

        userCertificateTree = filterUnrelatedCertifications(competenceProfile, tree).map(extendBranchWithCertificates);

        // Fallback:
        if (userCertificates.length) {
            // Any unmatched certifications should simply be added to the tree.
            // This is mostly required because we switched from certifying any profile to only the main (non-variant) profiles.
            const extraCertificates = userCertificates.filter(c => !certifiedProfiles.has(c.competence_profile.uuid)).map((c) => ({
                ...c,
                certificate: c,
                children: []
            }))
            userCertificateTree = userCertificateTree.concat(extraCertificates);
            certifiedProfiles.add(extraCertificates.map((c) => c.competence_profile.uuid));
        }
    }


    const cancelStudentFromCourse = useCallback(
        async (uuid, name) => {
            const courseUUID = props?.contextData?.courseDetail?.uuid;

            const resultDescription = `De student '${name}' is van de cursus geannuleerd.`;

            let result = await remoteCancelStudent(courseUUID, uuid);

            if (result?.errors?.length > 0) {
                dispatch(
                    addGlobalMessage({
                        title: result?.errors?.response?.data || "Er is een onbekende fout opgetreden",
                        type: "error",
                        icon: "fa fa-exclamation-triangle"
                    })
                );
            } else {
                dispatch(
                    addGlobalMessage({
                        title: resultDescription,
                        link: `/courses/edit/${courseUUID}/students/`
                    })
                );
                if (typeof props?.refresh === "function") {
                    props?.contextData?.endTermsMutate();
                    props?.refresh();
                    props.unSelectInBulk(uuid);
                }
            }
        },
        [props?.refresh, props?.contextData?.courseDetail?.uuid]
    );

    const getStudentName = (data) => {
        return `${data.initials || ""} ${data.first_name && data.first_name !== data.initials ? `(${data.first_name})` : ""} ${data.last_name_prefix || ""} ${
            data.last_name || ""
        }`.trim();
    };

    const isCourseProfileAvailable = () => {
        if (props.cancelled) return false;

        if (props.latest_status === "achieved") return true;

        const certification = props.available_certifications.find((c) => c.competence_profile.uuid === props.contextData.courseDetail.competence_profile);

        return (
            props.exemptions?.length === props.contextData?.endTerms?.length ||
            (certification?.available && certification?.competence_profile?.current_certification?.course_id === props.contextData.courseDetail.id)
        );
    };

    return (
        <tr key={props.uuid} className={props.cancelled ? "lighter" : cx("")}>
            {props?.bulkColumn && props.bulkColumn}

            {props?.columns.some((col) => col.columnIdentifier === "nibhv_number") && <td className={"lighter"}>{props.nibhv_number}</td>}
            {props?.columns.some((col) => col.columnIdentifier === "full_name") && (
                <td>
                    <a
                        href={props.cancelled ? "#" : `/students/view/${props.uuid}/`}
                        target={"_blank"}
                        className={props.cancelled ? "unclickable-link" : "link--blue"}
                        title={"Bekijk cursistinformatie"}
                    >
                        <strong title={props.full_name}>
                            <div className="overview-redux__truncated-text overview-redux__truncated-text--with-icon">
                                {getStudentName(props)}
                                {!props.cancelled && (
                                    <>
                                        <i className={cx("fa fa-external-link overview-redux__truncated-text-icon")} data-tip data-for={`student-info-${props.uuid}`} />
                                        <ReactTooltip id={`student-info-${props.uuid}`} effect="solid" place="left" delayShow={250} delayHide={125}>
                                            Bekijk cursistinformatie
                                        </ReactTooltip>
                                    </>
                                )}
                            </div>
                        </strong>
                    </a>
                    <div className="overview-redux__truncated-text" title={props.email}>
                        {props.email}
                    </div>

                    {userCertificateTree.length > 0 &&
                        (certifiedProfiles.size > 0 ? (
                            <details open className="overview-redux__details">
                                <summary>Certificeringen ({certifiedProfiles.size})</summary>
                                <CertificatesList nodes={userCertificateTree} user={props} />
                            </details>
                        ) : (
                            // "No certifications available yet"
                            <p className="overview-redux__details text-muted">Nog geen certificeringen beschikbaar</p>
                        ))}

                    {certifiedProfiles.size >= 1 && (
                        <span>
                            <div
                                className="btn btn-secondary download-certificates-button"
                                onClick={() => downloadCertificates(props.contextData.courseDetail.uuid, props.uuid)}
                            >
                                Download zip
                            </div>
                        </span>
                    )}
                </td>
            )}
            {props?.columns.some((col) => col.columnIdentifier === "attendance") && (
                <td>
                    <AttendanceToggle
                        isCancelled={!!props.cancelled}
                        userUUID={props?.uuid}
                        courseUUID={props?.contextData?.courseDetail?.uuid}
                        name="attended"
                        enabled={!!props?.attended}
                        onSuccess={() => {
                            props.contextData.courseDetailMutate();
                            props?.refresh();
                        }}
                    />
                </td>
            )}
            {/*{props?.columns.some(col => col.columnIdentifier === "email") && (*/}
            {/*    <td>*/}
            {/*    </td>*/}
            {/*)}*/}
            {props?.columns.some((col) => col.columnIdentifier === "last_notified") && (
                <td>
                    {props.last_notified && (
                        <>
                            <i className="fa fa-check green" data-tip data-for={`last-notified-${props.uuid}`} />
                            <ReactTooltip id={`last-notified-${props.uuid}`} effect="solid" place="top" delayShow={250} delayHide={125}>
                                {moment(props.last_notified.notified).format("[Op] DD-MM-YYYY [om] HH:mm")} door {props.last_notified.notified_by.full_name}
                            </ReactTooltip>
                        </>
                    )}
                </td>
            )}
            {props?.columns.some((col) => col.columnIdentifier === "insured") && (
                <td>
                    <UserOptionToggle
                        disabled={!!props.cancelled}
                        userUUID={props?.uuid}
                        courseUUID={props?.contextData?.courseDetail?.uuid}
                        name={"insured"}
                        enabled={!!props?.student_options?.insured}
                        onSuccess={props?.refresh}
                    />
                </td>
            )}
            {props?.columns.some((col) => col.columnIdentifier === "employer_specific") && (
                <td>
                    <UserOptionToggle
                        disabled={!!props.cancelled}
                        userUUID={props?.uuid}
                        courseUUID={props?.contextData?.courseDetail?.uuid}
                        name={"employer_specific"}
                        enabled={!!props?.student_options?.employer_specific}
                        onSuccess={props?.refresh}
                    />
                </td>
            )}
            {props?.columns.some((col) => col.columnIdentifier === "elearning_status") && (
                <td>
                    {props.elearning_status === "new" ? " Nog niet begonnen" : false}
                    {props.elearning_status === "active" ? " Nog bezig" : false}
                    {props.elearning_status === "not-achieved" ? " Nog bezig" : false}
                    {props.elearning_status === "achieved" ? " Behaald" : false}
                </td>
            )}
            {props?.columns.some((col) => col.columnIdentifier === "latest_status") && (
                <td>
                    {props.latest_status === "no-progress" ? "Nog niet behaald" : false}
                    {props.latest_status === "not-achieved" ? "Bezig" : false}
                    {props.latest_status === "achieved" ? "Behaald" : false}
                </td>
            )}
            {props?.columns.some((col) => col.columnIdentifier === "date_of_birth") && (
                <td>{props.date_of_birth === null ? "-" : moment(props.date_of_birth).format("DD-MM-YYYY")}</td>
            )}
            {props?.columns.some((col) => col.columnIdentifier === "exemptions") && (
                <td>
                    {props?.contextData?.endTerms?.map((endTerm) => {
                        return (
                            <EndTermToggle
                                disabled={() => {
                                    if (!props.exemptions?.length) return false;

                                    const exemption = props.exemptions.find((e) => e.end_term === endTerm.uuid);
                                    return exemption ? exemption.has_results || !exemption.can_be_deleted : false;
                                }}
                                isCancelled={!!props.cancelled}
                                type={"checkbox"}
                                key={endTerm.uuid}
                                userUUID={props?.uuid}
                                courseUUID={props?.contextData?.courseDetail?.uuid}
                                competenceProfileUUID={props?.contextData?.courseDetail?.competence_profile}
                                endTerm={endTerm}
                                exemption={props.exemptions.find((e) => e.end_term === endTerm?.uuid)}
                                onSuccess={() => {
                                    // when we toggle, both endTerms and the overview should be mutated as this info relies on it
                                    props?.contextData?.endTermsMutate();
                                    props.contextData.courseDetailMutate();
                                    props?.refresh();
                                }}
                            />
                        );
                    })}
                </td>
            )}

            <td>
                {displayStudent && (
                    <div className="flex flex-justify-end flex-align-center">
                        <EventStudent
                            visible={displayStudent}
                            studentUUID={props?.uuid}
                            isInsured={props?.student_options?.insured}
                            isEmployerSpecific={props?.student_options?.employer_specific}
                            course={props?.contextData?.courseDetail}
                            events={props?.contextData?.eventDetails?.results || []}
                            parentGetEvents={() => {
                                // when something changes, both endTerms and the overview should be mutated as this info relies on it
                                props?.contextData?.endTermsMutate();
                                props.refresh();
                                window.location.reload();
                            }}
                            closeStudentDialog={() => setDisplayStudent(false)}
                        />
                    </div>
                )}
                <div className="overview-redux__buttons">
                    <ReactTooltip id={`student-course-info-button-${props.uuid}`} effect="solid" place={"top"} delayShow={250} delayHide={125}>
                        Bekijk de cursus gegevens van de cursist.
                    </ReactTooltip>
                    <i
                        className={cx("fa fa-line-chart overview-redux__button", "course-student-overview-chart", {
                            "course-student-overview-chart--achieved": isCourseProfileAvailable(),
                            "course-student-overview-chart--open": props?.latest_status !== "achieved"
                        })}
                        data-tip
                        data-for={`student-course-info-button-${props.uuid}`}
                        onClick={(e) => {
                            setDisplayStudent(true);
                        }}
                    />

                    {props.is_removable ? (
                        <>
                            <ReactTooltip id={`remove-course-student-${props.uuid}`} effect="solid" place={"top"} delayShow={250} delayHide={125}>
                                Verwijder cursist van deze cursus
                            </ReactTooltip>
                            <i
                                className="fa fa-trash overview-redux__button"
                                data-tip
                                data-for={`remove-course-student-${props.uuid}`}
                                onClick={() => props.contextData.setNotifyStudentModalOpen({ action: "delete", user: props })}
                            />
                        </>
                    ) : (
                        <>
                            {!props.cancelled ? (
                                <ReactTooltip id={`cancel-course-student-${props.uuid}`} effect="solid" place={"top"} delayShow={250} delayHide={125}>
                                    Annuleer cursist van deze cursus
                                </ReactTooltip>
                            ) : (
                                <ReactTooltip id={`cancel-course-student-${props.uuid}`} effect="solid" place={"top"} delayShow={250} delayHide={125}>
                                    Heractiveer cursist voor deze cursus
                                </ReactTooltip>
                            )}
                            <i
                                style={{ color: props.cancelled ? "green" : "red" }}
                                className="fa fa-ban overview-redux__button"
                                data-tip
                                data-for={`cancel-course-student-${props.uuid}`}
                                onClick={async () => {
                                    if (props.cancelled) {
                                        props.contextData.setNotifyStudentModalOpen({ action: "uncancel", user: props });
                                        return;
                                    }
                                    props.contextData.setNotifyStudentModalOpen({ action: "cancel", user: props });
                                }}
                            />
                        </>
                    )}

                    <NotifyUserDialog
                        removeStudentFromCourse={async (uuid, name) => await removeStudentFromCourse(uuid, name, props.exemptions && props.exemptions.length)}
                        cancelStudentFromCourse={async (uuid, name) => await cancelStudentFromCourse(uuid, name, props.exemptions && props.exemptions.length)}
                        uncancelStudentFromCourse={async (uuid) => {
                            await axios.patch(`/v1/courses/${props?.contextData?.courseDetail?.uuid}/students/${uuid}/`, {
                                cancelled: false
                            });
                            props.refresh();
                        }}
                        onClose={() => props.contextData.setNotifyStudentModalOpen(false)}
                        open={props.contextData.notifyStudentModalOpen}
                        course={props.contextData.courseDetail}
                        user={props}
                        onSuccess={(mode, shouldNotifyUser) => {
                            props.contextData.setNotifyStudentModalOpen(false);
                            let title;
                            if (mode === "cancel") title = "Cursist is op de hoogte gesteld van zijn/haar annulering op de cursus.";
                            else if (mode === "delete") title = "Cursist is op de hoogte gesteld van zijn/haar verwijdering op de cursus.";
                            else if (mode === "uncancel") title = "Cursist is op de hoogte gesteld van zijn/haar heractivatie op de cursus.";

                            if (shouldNotifyUser)
                                dispatch(
                                    addGlobalMessage({
                                        title,
                                        link: "/courses"
                                    })
                                );
                        }}
                        onError={() => {
                            props.contextData.setNotifyStudentModalOpen(false);
                            dispatch(
                                addGlobalMessage({
                                    title: "Er is een onbekende fout opgetreden",
                                    type: "error",
                                    icon: "fa fa-exclamation-triangle"
                                })
                            );
                        }}
                    />
                </div>
            </td>
        </tr>
    );
};

export const CourseStudentListDefinitionBase = {
    queryFn: getCourseStudents,
    search: {
        columnIdentifier: "search",
        placeholder: "Zoek op naam, e-mail of NIBHV nummer"
    },
    bulkEnabled: true,
    bulkPreselectAll: true,
    bulkOptions: [
        {
            bulkIdentifier: "copy-students",
            icon: "fa-clone",
            name: "Kopieer cursisten naar andere cursus",
            callback: ({ items, contextData, onSuccess, refresh }) => {
                contextData?.setDisplayCopyStudentToCourse({ items, contextData, onSuccess, refresh });
            }
        },
        {
            bulkIdentifier: "invite-students",
            icon: "fa-envelope-o",
            name: "Nodig cursisten uit",
            callback: ({ items, contextData, onSuccess, refresh, bulkData }) => {
                contextData?.setDisplayNotifyStudents({ items, contextData, onSuccess, refresh, bulkData });
            }
        },
        {
            bulkIdentifier: "insure-students",
            icon: "fa-shield",
            name: "Verzeker cursisten",
            callback: ({ items, contextData, onSuccess, refresh }) => {
                contextData?.setDisplayInsureStudents({ items, contextData, onSuccess, refresh });
            }
        },
        {
            bulkIdentifier: "business-specific-students",
            icon: "fa-building-o",
            name: "Markeer cursus voor cursisten als bedrijfsspecifiek",
            callback: ({ items, contextData, onSuccess, refresh }) => {
                contextData?.setBusinessSpecificStudents({ items, contextData, onSuccess, refresh });
            }
        },
        {
            bulkIdentifier: "gradesheet-download-students",
            icon: "fa-line-chart",
            name: "Download cijferlijst voor de geselecteerde studenten",
            callback: ({ items, contextData }) => {
                contextData?.downloadGradeSheet(items);
            }
        }
    ],
    bulkValue: (obj) => obj.uuid,
    // title: "Cursisten",
    title: (props) => {
        return (
            <h2 className="h1 mb-3 float-left">
                Cursisten ({props?.contextData?.courseDetail?.student_count} / {props?.contextData?.courseDetail?.expected_students})
            </h2>
        );
    },
    displaySummary: true,
    RowStructure,
    actions: [
        // {
        //     title: "Button",
        //     identifier: "button",
        //     icon: "fa fa-copy",
        //     link: "/",
        // },
        // {
        //     identifier: "link",
        //     Component: props => {
        //         return (
        //             <a href={"#"}>Simpele link in component</a>
        //         );
        //     }
        // },
        // {
        //     identifier: "copy-students",
        //     Component: props => {
        //         if(props?.overview?.data?.results?.length > 0) {
        //
        //             return (
        //                 <WrapInDialogButton
        //                     buttonTitle={"Cursisten kopiëren"}
        //                     className={"btn btn-primary no-sideflap"}
        //                     icon="copy"
        //                     triggerOnly={true} >
        //                     <CopyStudents
        //                         {...props}
        //                         moveStudentDialog={true}
        //                         course={props.contextData?.courseDetail}
        //                         getData={() => mutateStudents()}
        //                         students={props?.overview?.data?.results || []}
        //                     />
        //                 </WrapInDialogButton>
        //             );
        //         }
        //         return null;
        //     }
        // },
        // Old style (button above the list) for inviting students
        // {
        //     identifier: "invite-students",
        //     Component: props => {
        //         if(canAddStudents(props?.contextData?.courseDetail) && props?.overview?.data?.results?.length > 0) {
        //             return <WrapInDialogButton
        //                 noClose
        //                 buttonTitle="Cursisten uitnodigen"
        //                 icon="envelope"
        //                 dialogTitle="Cursisten uitnodigen"
        //                 className="btn btn-primary no-sideflap">
        //                 <NotifyStudents
        //                     course={props.contextData?.courseDetail}
        //                     users={props?.overview?.data?.results || []}
        //                     onSuccess={(count) => {
        //                         if (count > 0) {
        //                             props?.contextData?.addGlobalMessage({
        //                                 title: `${count} ${count === 1 ? "cursist is" : "cursisten zijn"} uitgenodigd`
        //                             });
        //                             props.refresh();
        //                         }
        //                     }}
        //                     // onError={props.onError}
        //                 />
        //             </WrapInDialogButton>
        //         }
        //         return null;
        //     }
        //
        // },
        {
            identifier: "download-student-sheet",
            Component: (props) => {
                return (
                    <>
                        <button
                            data-tip
                            data-for="print-students-icon"
                            className="btn btn-primary no-sideflap print-students-button"
                            disabled={props.contextData.pdfDownloadLoading}
                            onClick={async () => {
                                props.contextData.setPDFDownloadLoading(true);

                                downloadFile(
                                    `/v1/courses/${props.contextData?.courseDetail?.uuid}/students.pdf/`,
                                    `${props?.contextData?.courseDetail.display_name.replaceAll(" ", "_")}_attendance_sheet.pdf`
                                ).finally(() => {
                                    props.contextData.setPDFDownloadLoading(false);
                                });
                            }}
                        >
                            Deelnemerslijst afdrukken
                            {props.contextData.pdfDownloadLoading ? <div className="print-students-loader"></div> : <i className="fa fa-list"></i>}
                        </button>
                        <ReactTooltip id="print-students-icon" effect="solid" place="top" delayShow={250} delayHide={125}>
                            Deelnemerslijst afdrukken
                        </ReactTooltip>
                    </>
                );
            }
        },
        {
            identifier: "import-students",
            Component: (props) => {
                if (canAddStudents(props?.contextData?.courseDetail)) {
                    return (
                        <WrapInDialogButton
                            buttonDisabled={props.contextData?.courseDetail.state === "finished"}
                            buttonTitle={"Cursisten importeren"}
                            icon="download"
                            dialogTitle={""}
                            className={"btn btn-primary no-sideflap"}
                        >
                            <ImportStudent course={props?.contextData?.courseDetail} refreshData={() => props?.refresh()} />
                        </WrapInDialogButton>
                    );
                }
                return null;
            }
        },
        {
            identifier: "find-student",
            Component: (props) => {
                if (canAddStudents(props?.contextData?.courseDetail)) {
                    return (
                        <FindStudent
                            course={props?.contextData?.courseDetail}
                            events={props?.contextData?.courseDetail?.events || []}
                            addGlobalMessage={props?.contextData?.addGlobalMessage}
                            getData={() => {
                                props?.refresh();
                            }}
                        />
                    );
                }
                return null;
            }
        }
    ],
    columns: [
        {
            title: "NIBHV #",
            columnIdentifier: "nibhv_number",
            className: "",
            orderable: false
            // Filter: TextFilter,
        },
        {
            title: "Naam",
            columnIdentifier: "full_name",
            className: "",
            orderable: false
            // Filter: TextFilter,
        },
        // {
        //     title: "",
        //     columnIdentifier: "attendance",
        //     orderable: false,
        //     Title: (props) => {
        //         const allStudentsAttending = props.contextData.courseDetail.students.every((student) => student.attended);
        //         return (
        //             (
        //                 <>
        //                     <span className="attendance-group-heading">Aanwezigheid</span>
        //                     <i
        //                         data-tip
        //                         data-for="attendance-check-icon"
        //                         className="fa fa-check save-all-exemptions-icon"
        //                         onClick={async () => {
        //                             const url = `/v1/courses/${props.contextData?.courseDetail?.uuid}/students/`;

        //                             const attendAllMessage = "Weet je zeker dat je alle studenten als 'aanwezig' wilt markeren voor elk toetsmoment?";
        //                             const unAttendAllMessage = "Weet je zeker dat je alle studenten als 'afwezig' wilt markeren voor elk toetsmoment?";

        //                             const allStudentsAttending = props.contextData.courseDetail.students.every((student) => student.attended);

        //                             const question = allStudentsAttending ? unAttendAllMessage : attendAllMessage;

        //                             let postData = [];
        //                             props.contextData.courseDetail.students.forEach((student) => {
        //                                 if (!student.cancelled) {
        //                                     postData.push({
        //                                         uuid: student.uuid,
        //                                         attended: allStudentsAttending ? false : true
        //                                     });
        //                                 }
        //                             });

        //                             if (confirm(question)) {
        //                                 try {
        //                                     await axios.put(url, postData);
        //                                     props.contextData.courseDetailMutate();
        //                                     refreshFn();
        //                                 } catch (error) {
        //                                     console.log(error);
        //                                 }
        //                             }
        //                         }}
        //                     />
        //                     {allStudentsAttending ? (
        //                         <ReactTooltip id="attendance-check-icon" effect="solid" place="top" delayShow={250} delayHide={125}>
        //                             Markeer alle studenten als 'afwezig'
        //                         </ReactTooltip>
        //                     ) : (
        //                         <ReactTooltip id="attendance-check-icon" effect="solid" place="top" delayShow={250} delayHide={125}>
        //                             Markeer alle studenten als 'aanwezig'
        //                         </ReactTooltip>
        //                     )}
        //                 </>
        //             ) || null
        //         );
        //     }
        // },
        // {
        //     title: "E-mail",
        //     columnIdentifier : "email",
        //     className: "",
        //     orderable: false,
        //     // Filter: TextFilter,
        //
        // },
        {
            title: "",
            columnIdentifier: "last_notified",
            className: "",
            orderable: false,
            // Filter: TextFilter,
            Title: IconTitle,
            TitleOptions: {
                icon: "envelope",
                text: "Cursist heeft uitnodiging ontvangen"
            }
        },
        {
            title: "",
            columnIdentifier: "insured",
            className: "",
            Title: IconTitle,
            TitleOptions: {
                icon: "shield",
                text: "Cursist is verzekerd voor deze cursus "
            }
        },
        {
            title: "",
            columnIdentifier: "employer_specific",
            className: "",
            Title: IconTitle,
            TitleOptions: {
                icon: "building",
                text: "Cursist ontvangt een bedrijfsspecifiek certificaat"
            }
        },
        {
            title: "E-learning",
            columnIdentifier: "elearning_status",
            className: "",
            orderable: false
            // Filter: TextFilter,
        },
        // {
        //     title: "Cursusstatus",
        //     columnIdentifier : "latest_status",
        //     className: "",
        //     orderable: false,
        //     // Filter: TextFilter,
        // },
        {
            title: "Geb. Datum",
            columnIdentifier: "date_of_birth",
            className: "",
            orderable: false
            // Filter: TextFilter,
        },
        {
            title: "",
            columnIdentifier: "exemptions",
            classNameFn: (props) => {
                return `overview-redux__head--exemptions-${props?.contextData?.endTerms?.length > 2 ? props.contextData.endTerms.length : 2}`;
            },
            orderable: false,
            Title: (props) => {
                return (
                    (
                        <>
                            <span className="exemptions-group-heading">Vrijstellingen</span>
                            {props?.contextData?.endTerms?.map((endTerm) => {
                                const isDisabled = props.contextData.studentDetails?.results.some((student) =>
                                    student.exemptions?.some((ex) => ex.end_term === endTerm.uuid && ex.can_be_deleted === false)
                                );

                                return (
                                    <div className="end-term-container" key={endTerm.uuid}>
                                        <span style={{ display: "block" }} data-tip data-for={"end-term-" + endTerm.uuid}>
                                            {endTerm?.abbreviation || endTerm?.name?.[0] || "-"}
                                        </span>
                                        {endTerm.can_be_exempted ? (
                                            <i
                                                className="fa fa-check save-all-exemptions-icon"
                                                onClick={async () => {
                                                    if (isDisabled) return;
                                                    const url = `/v1/courses/${props.contextData?.courseDetail?.uuid}/exemptions/?end_term=${endTerm.uuid}`;
                                                    const exemptAllMessage = `Weet u zeker dat u alle studenten wilt vrijstellen van ${endTerm?.name}?`;
                                                    const unexemptAllMessage = `Weet je zeker dat je alle studenten wilt deselecteren voor de gegeven vrijstelling(en)?`;

                                                    const allStudentsHaveExemption = props.contextData.courseDetail.students.every((student) =>
                                                        student.exemptions.some((ex) => ex.end_term === endTerm.uuid)
                                                    );

                                                    const question = allStudentsHaveExemption ? unexemptAllMessage : exemptAllMessage;

                                                    let postData = [];
                                                    props.contextData.courseDetail.students.map((student) => {
                                                        if (!student.cancelled) {
                                                            postData.push({
                                                                user: student.uuid,
                                                                end_term: endTerm.uuid,
                                                                // if all students have the exemptions, we instead opt to delete them:
                                                                delete: allStudentsHaveExemption
                                                            });
                                                        }
                                                    });

                                                    if (confirm(question)) {
                                                        try {
                                                            await axios.put(url, postData);
                                                            props.contextData.courseDetailMutate();
                                                            refreshFn();
                                                        } catch (error) {
                                                            if (error?.response?.data?.detail?.includes("Unable to remove the exemption of end term")) {
                                                                props.contextData.addGlobalMessage({
                                                                    title: "Deze vrijstelling kan niet worden verwijderd omdat er al meetresultaten door zijn gemaakt.",
                                                                    type: "error",
                                                                    icon: "fa fa-exclamation-triangle"
                                                                });
                                                                smoothScrollToTop(600);
                                                            }
                                                            console.log(error);
                                                        }
                                                    }
                                                }}
                                                style={{ cursor: isDisabled ? "not-allowed" : "pointer", opacity: isDisabled ? 0.5 : 1 }}
                                            />
                                        ) : (
                                            <i className="fa fa-check save-all-exemptions-icon-disabled"></i>
                                        )}
                                        <ReactTooltip id={"end-term-" + endTerm.uuid} effect={"solid"} place={"top"} delayShow={250} delayHide={125}>
                                            Vrijstelling voor: {endTerm?.name}
                                        </ReactTooltip>
                                    </div>
                                );
                            })}
                        </>
                    ) || null
                );
            }
        },

        {
            title: "Actie",
            columnIdentifier: "action-column"
        }
    ],
    additionalFields: ["uuid"],
    defaults: {
        ordering: {
            columnIdentifier: "full_name",
            direction: ""
        },
        filter: {}
    },
    pagination: {
        cursorPaginator: {
            size: 20,
            loadMore: true
        }
    }
};

const generateDownloadLink = (url, name, iconClass) => (
    <a
        href={url}
        onClick={(event) => {
            event.preventDefault();
            downloadFile(`${APP_CONFIG.api_url}${url}`);
        }}
        target="_blank"
        rel="noopener noreferrer"
    >
        {name}
        <i className={iconClass} />
    </a>
);

const READY_STATE = "ready";

const CertificateItem = ({ node }) => {
    const { certificate, competence_profile } = node;
    const { state, url, digital_pass_url, course } = certificate || {};
    const { name, certification_name } = competence_profile || {};
    const nameVar = !!certification_name ? certification_name : name;

    if (state === READY_STATE) {
        return (
            <div>
                {generateDownloadLink(url, nameVar, "fa fa-download")}
                {APP_CONFIG.combineCertificates && course && course?.uuid !== courseUUID && `(${course?.id})`}
                {digital_pass_url && (
                    <>
                        <br />
                        {generateDownloadLink(digital_pass_url, `Pas - ${nameVar}`, "fa fa-ticket")}{" "}
                        {APP_CONFIG.combineCertificates && course && course?.uuid !== courseUUID && `(${course?.id})`}
                    </>
                )}
            </div>
        );
    }

    return nameVar;
};

const CertificatesList = ({ nodes, user }) => {
    if (!nodes || nodes.length === 0) {
        return null;
    }

    const hasCertificate = (node) => node.certificate || (node.children && node.children.some(hasCertificate));

    return (
        <ul className="overview-redux__data-list">
            {nodes.filter(hasCertificate).map((node) => (
                <li key={node.competence_profile.uuid} style={{ display: "block" }}>
                    <CertificateItem node={node} />
                    <CertificatesList nodes={node.children} user={user} />
                </li>
            ))}
        </ul>
    );
};
